
import { 
  collection, 
  getDocs, 
  query, 
  where,
  orderBy,
  FirestoreError
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Lesson } from '@/models/Lesson';
import { toast } from '@/lib/toast';
import { FirestoreService } from '../base/FirestoreService';

export class LessonQueryService extends FirestoreService {
  constructor() {
    super('lessons');
  }

  // Get all active lessons (for regular users)
  async getActiveLessons(): Promise<Lesson[]> {
    try {
      const q = query(
        collection(db, this.collectionName),
        where('active', '==', true),
        orderBy('order', 'asc')
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name,
        words: doc.data().words || [],
        active: doc.data().active !== undefined ? doc.data().active : true,
        order: doc.data().order || 0,
        createdAt: doc.data().createdAt,
        updatedAt: doc.data().updatedAt,
        lastCompletedAt: doc.data().lastCompletedAt,
        userId: doc.data().userId
      }));
    } catch (error) {
      const firestoreError = error as FirestoreError;
      console.error('Error fetching lessons:', firestoreError);
      toast.error('Failed to load lessons');
      return [];
    }
  }

  // Get user's lessons (both created by user and active lessons)
  async getUserLessons(userId: string): Promise<Lesson[]> {
    try {
      // First, get all active lessons
      const activeQ = query(
        collection(db, this.collectionName),
        where('active', '==', true),
        orderBy('order', 'asc')
      );
      
      const activeSnapshot = await getDocs(activeQ);
      const activeLessons = activeSnapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name,
        words: doc.data().words || [],
        active: doc.data().active !== undefined ? doc.data().active : true,
        order: doc.data().order || 0,
        createdAt: doc.data().createdAt,
        updatedAt: doc.data().updatedAt,
        lastCompletedAt: doc.data().lastCompletedAt,
        userId: doc.data().userId
      }));
      
      return activeLessons;
    } catch (error) {
      const firestoreError = error as FirestoreError;
      console.error('Error fetching user lessons:', firestoreError);
      toast.error('Failed to load lessons');
      return [];
    }
  }

  // Get all lessons (for admin users)
  async getAllLessons(): Promise<Lesson[]> {
    try {
      const q = query(
        collection(db, this.collectionName),
        orderBy('order', 'asc'),
        orderBy('createdAt', 'desc')
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        name: doc.data().name,
        words: doc.data().words || [],
        active: doc.data().active !== undefined ? doc.data().active : true,
        order: doc.data().order || 0,
        createdAt: doc.data().createdAt,
        updatedAt: doc.data().updatedAt,
        lastCompletedAt: doc.data().lastCompletedAt,
        userId: doc.data().userId
      }));
    } catch (error) {
      const firestoreError = error as FirestoreError;
      console.error('Error fetching lessons:', firestoreError);
      toast.error('Failed to load lessons');
      return [];
    }
  }

  // Get a single lesson by ID
  async getLesson(lessonId: string): Promise<Lesson | null> {
    return this.getDocById<Lesson>(lessonId);
  }
}
